import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const mobileScrollLockQuery = window.matchMedia('(max-width: 749px)');
const openOverlaySelector = [
  'dialog.dialog-modal[open]',
  'dialog.dialog-drawer[open]',
  'dialog[scroll-lock][open]',
  'details[scroll-lock][open]',
  '.cart-drawer__dialog[open]',
  '.custom-popup__dialog[open]',
  '.facets-drawer[open]',
  '.facets-drawer[aria-hidden="false"]',
  '.mobile-filters__dialog[open]',
  '.search-modal__content[open]',
  '.quick-add-modal[open]',
  '.size-chart-popup[open]',
  '#pdp-size-guide[open]',
  '#quickview-size-guide[open]',
].join(',');
const scrollableOverlaySelector = [
  'dialog[open]',
  '.dialog-modal[open]',
  '.dialog-drawer[open]',
  'details[scroll-lock][open] .menu-drawer',
  '.menu-open > .menu-drawer',
  '.drawer[open]',
  '.drawer[aria-hidden="false"]',
  '.cart-drawer__dialog[open]',
  '.search-modal__content[open]',
  '.quick-add-modal[open]',
  '.size-chart-popup[open]',
  '.custom-popup__dialog[open]',
].join(',');
const scrollableContentSelector = [
  '.drawer__inner',
  '.drawer__content',
  '.cart-drawer__inner',
  '.cart-drawer__content',
  '.cart-drawer__items',
  '.menu-drawer',
  '.mobile-mega-menu__panels',
  '.mobile-mega-menu__panel.is-active',
  '.mobile-mega-menu__panel-body',
  '.facets-drawer__form-wrapper',
  '.facets-drawer__filters',
  '.mobile-filters__body',
  '.search-modal__content',
  '.predictive-search-form__content-wrapper',
  '.quick-add-modal__content',
  '.drawer-content',
  '.luxe-deal-popup__content',
  '.modal__content',
].join(',');
const themeDialogSelector = [
  'dialog.dialog-modal',
  'dialog.dialog-drawer',
  'dialog[scroll-lock]',
  '.cart-drawer__dialog',
  '.custom-popup__dialog',
  '.mobile-filters__dialog',
  '.facets-drawer',
  '.search-modal__content',
  '.quick-add-modal',
  '.size-chart-popup',
  '#pdp-size-guide',
  '#quickview-size-guide',
].join(',');

let isMobileScrollLocked = false;
let mobileScrollY = 0;

/**
 * @returns {number}
 */
function getCurrentScrollY() {
  if (document.body.style.position === 'fixed') {
    const lockedTop = Number.parseFloat(document.body.style.top || '0');

    if (Number.isFinite(lockedTop)) return Math.abs(lockedTop);
  }

  return window.scrollY;
}

/**
 * @param {number} scrollY
 */
function restoreScrollY(scrollY) {
  window.scrollTo({ top: scrollY, behavior: 'auto' });
}

/**
 * @returns {boolean}
 */
function hasOpenOverlay() {
  return Boolean(document.querySelector(openOverlaySelector));
}

/**
 * @param {unknown} dialog
 * @returns {dialog is HTMLDialogElement}
 */
function isThemeScrollLockDialog(dialog) {
  return dialog instanceof HTMLDialogElement && dialog.matches(themeDialogSelector);
}

function lockMobileScroll() {
  if (isMobileScrollLocked || !mobileScrollLockQuery.matches || !document.body) return;

  mobileScrollY = getCurrentScrollY();
  document.documentElement.setAttribute('scroll-lock', '');
  document.documentElement.classList.add('mobile-scroll-locked');
  document.body.classList.add('mobile-scroll-locked');
  document.documentElement.style.overflow = 'hidden';
  document.body.style.setProperty('--mobile-scroll-y', `-${mobileScrollY}px`);
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${mobileScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
  isMobileScrollLocked = true;
}

function unlockMobileScroll() {
  if (!isMobileScrollLocked) {
    document.documentElement.removeAttribute('scroll-lock');
    return;
  }

  const lockedTop = Number.parseInt(document.body.style.top || '0', 10);
  const scrollY = Math.abs(lockedTop) || mobileScrollY || window.scrollY;
  const previousScrollBehavior = document.documentElement.style.scrollBehavior;

  document.documentElement.style.scrollBehavior = 'auto';
  document.documentElement.style.overflow = '';
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';

  restoreScrollY(scrollY);
  document.documentElement.removeAttribute('scroll-lock');
  document.documentElement.classList.remove('mobile-scroll-locked');
  document.body.classList.remove('mobile-scroll-locked');
  document.body.style.removeProperty('--mobile-scroll-y');
  isMobileScrollLocked = false;
  mobileScrollY = 0;

  requestAnimationFrame(() => {
    restoreScrollY(scrollY);

    requestAnimationFrame(() => {
      restoreScrollY(scrollY);
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    });
  });
}

function updateMobileScrollLock() {
  if (mobileScrollLockQuery.matches && hasOpenOverlay()) {
    lockMobileScroll();
    return;
  }

  unlockMobileScroll();
}

/**
 * @param {EventTarget | null} target
 * @returns {Element | null}
 */
function getScrollableOverlay(target) {
  if (!(target instanceof Element)) return null;

  return target.closest(scrollableOverlaySelector);
}

/**
 * @param {EventTarget | null} target
 * @returns {Element | null}
 */
function getScrollableContent(target) {
  if (!(target instanceof Element)) return null;

  return target.closest(scrollableContentSelector);
}

/**
 * @param {TouchEvent} event
 */
function handleLockedTouchMove(event) {
  if (!isMobileScrollLocked || !mobileScrollLockQuery.matches) return;
  if (!getScrollableOverlay(event.target) || !getScrollableContent(event.target)) {
    event.preventDefault();
  }
}

function refreshMobileScrollLockSoon() {
  requestAnimationFrame(updateMobileScrollLock);
}

window.DarveysMobileScrollLock = {
  lock: lockMobileScroll,
  unlock: () => {
    if (hasOpenOverlay()) return;
    unlockMobileScroll();
  },
  refresh: updateMobileScrollLock,
  isLocked: () => isMobileScrollLocked,
};

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    this.#showDialog({ immediate: false });
  }

  /**
   * Shows the dialog without deferring the native `showModal()` call.
   *
   * iOS Safari only opens the software keyboard when input focus happens in
   * the same user gesture, so search uses this path before focusing its input.
   */
  showDialogImmediately() {
    this.#showDialog({ immediate: true });
  }

  /**
   * @param {{ immediate: boolean }} options
   */
  #showDialog(options) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const openDialog = () => {
      dialog.showModal();
      updateMobileScrollLock();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    };

    if (options.immediate) {
      openDialog();
      return;
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes.
    requestAnimationFrame(openDialog);
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    updateMobileScrollLock();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          updateMobileScrollLock();
        } else {
          requestAnimationFrame(updateMobileScrollLock);
        }
      }
    }
  },
  { capture: true }
);

const mobileScrollLockObserver = new MutationObserver(() => {
  refreshMobileScrollLockSoon();
});

mobileScrollLockObserver.observe(document.documentElement, {
  subtree: true,
  attributes: true,
  attributeFilter: ['open', 'aria-hidden'],
});

if (typeof mobileScrollLockQuery.addEventListener === 'function') {
  mobileScrollLockQuery.addEventListener('change', updateMobileScrollLock);
} else {
  mobileScrollLockQuery.addListener(updateMobileScrollLock);
}

document.addEventListener('touchmove', handleLockedTouchMove, { passive: false, capture: true });

if (window.HTMLDialogElement && !window.__darveysMobileDialogPatchApplied) {
  window.__darveysMobileDialogPatchApplied = true;

  const nativeShow = HTMLDialogElement.prototype.show;
  const nativeShowModal = HTMLDialogElement.prototype.showModal;
  const nativeClose = HTMLDialogElement.prototype.close;

  HTMLDialogElement.prototype.show = function () {
    const returnValue = nativeShow.call(this);
    if (isThemeScrollLockDialog(this)) {
      refreshMobileScrollLockSoon();
    }
    return returnValue;
  };

  HTMLDialogElement.prototype.showModal = function () {
    const returnValue = nativeShowModal.call(this);
    if (isThemeScrollLockDialog(this)) {
      updateMobileScrollLock();
    }
    return returnValue;
  };

  /**
   * @param {string} [returnValue]
   */
  HTMLDialogElement.prototype.close = function (returnValue) {
    const shouldRefresh = isThemeScrollLockDialog(this);
    const closeResult = nativeClose.call(this, returnValue);
    if (shouldRefresh) {
      refreshMobileScrollLockSoon();
    }
    return closeResult;
  };
}
